import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { QSR_DEMO_LOCATIONS } from '@/config/qsrConfig';
import { DEMO_CUSTOMERS } from '@/config/demoCustomers';
import { ALL_MODULES } from '@/config/moduleSectionMap';
import { useAuth } from '@/contexts/AuthContext';

// Industry-specific role configurations
export const INDUSTRY_ROLES = {
  hospitality: {
    owner: { id: 'venue-operator', label: 'Venue Operator (Owner)' },
    manager: { id: 'general-manager', label: 'General Manager' },
    roles: [{ id: 'operations-manager', label: 'Operations Manager', permissions: ['live_operations', 'management'] },
      { id: 'kitchen-manager', label: 'Kitchen Manager', permissions: ['management'] },
      { id: 'foh-manager', label: 'FOH Manager', permissions: ['live_operations', 'management', 'marketing_intelligence'] },
      { id: 'bar-manager', label: 'Bar Manager', permissions: ['management'] },
      { id: 'shift-supervisor', label: 'Shift Supervisor', permissions: ['live_operations'] },
      { id: 'hr-manager', label: 'HR Manager', permissions: ['management', 'staff'] },
      { id: 'team-lead', label: 'Team Lead', permissions: ['live_operations', 'management'] },
      { id: 'staff-member', label: 'Staff Member', permissions: ['live_operations'] },
      { id: 'compliance-officer', label: 'Compliance Officer', permissions: ['management', 'compliance'] },
      { id: 'marketing-manager', label: 'Marketing Manager', permissions: ['marketing_intelligence'] },
      { id: 'sales-manager', label: 'Sales Manager', permissions: ['live_operations', 'management'] },
      { id: 'accountant', label: 'Accountant', permissions: ['management', 'projections'] }
    ]
  },
  retail: {
    owner: { id: 'store-owner', label: 'Store Owner' },
    manager: { id: 'store-manager', label: 'Store Manager' },
    roles: [{ id: 'floor-manager', label: 'Floor Manager', permissions: ['live_operations', 'management'] },
      { id: 'stock-manager', label: 'Stock Manager', permissions: ['management'] },
      { id: 'visual-merchandiser', label: 'Visual Merchandiser', permissions: ['management', 'marketing_intelligence'] },
      { id: 'shift-lead', label: 'Shift Lead', permissions: ['live_operations'] }
    ]
  },
  healthcare: {
    owner: { id: 'practice-owner', label: 'Practice Owner' },
    manager: { id: 'practice-manager', label: 'Practice Manager' },
    roles: [{ id: 'clinical-director', label: 'Clinical Director', permissions: ['management'] },
      { id: 'patient-coordinator', label: 'Patient Coordinator', permissions: ['live_operations', 'management'] },
      { id: 'billing-manager', label: 'Billing Manager', permissions: ['management', 'projections'] },
      { id: 'reception-lead', label: 'Reception Lead', permissions: ['live_operations'] },
      { id: 'hr-manager', label: 'HR Manager', permissions: ['management', 'staff'] },
      { id: 'compliance-officer', label: 'Compliance Officer', permissions: ['management', 'compliance'] },
      { id: 'team-lead', label: 'Team Lead', permissions: ['live_operations', 'management'] },
      { id: 'staff-member', label: 'Staff Member', permissions: ['live_operations'] }
    ]
  },
  professional: {
    owner: { id: 'managing-partner', label: 'Managing Partner' },
    manager: { id: 'operations-director', label: 'Operations Director' },
    roles: [{ id: 'senior-consultant', label: 'Senior Consultant', permissions: ['management'] },
      { id: 'project-manager', label: 'Project Manager', permissions: ['live_operations', 'management'] },
      { id: 'business-analyst', label: 'Business Analyst', permissions: ['management', 'projections'] },
      { id: 'client-manager', label: 'Client Manager', permissions: ['live_operations', 'marketing_intelligence'] }
    ]
  },
  fitness: {
    owner: { id: 'gym-owner', label: 'Gym Owner' },
    manager: { id: 'club-manager', label: 'Club Manager' },
    roles: [{ id: 'fitness-director', label: 'Fitness Director', permissions: ['management'] },
      { id: 'pt-coordinator', label: 'PT Coordinator', permissions: ['live_operations', 'management'] },
      { id: 'membership-manager', label: 'Membership Manager', permissions: ['management', 'marketing_intelligence'] },
      { id: 'class-coordinator', label: 'Class Coordinator', permissions: ['live_operations'] }
    ]
  },
  property: {
    owner: { id: 'agency-principal', label: 'Agency Principal' },
    manager: { id: 'sales-director', label: 'Sales Director' },
    roles: [{ id: 'senior-agent', label: 'Senior Agent', permissions: ['management'] },
      { id: 'property-manager', label: 'Property Manager', permissions: ['live_operations', 'management'] },
      { id: 'marketing-coordinator', label: 'Marketing Coordinator', permissions: ['marketing_intelligence'] },
      { id: 'listing-coordinator', label: 'Listing Coordinator', permissions: ['live_operations'] }
    ]
  },
  construction: {
    owner: { id: 'company-director', label: 'Company Director' },
    manager: { id: 'project-director', label: 'Project Director' },
    roles: [{ id: 'site-manager', label: 'Site Manager', permissions: ['live_operations', 'management'] },
      { id: 'estimator', label: 'Estimator', permissions: ['management', 'projections'] },
      { id: 'safety-manager', label: 'Safety Manager', permissions: ['management'] },
      { id: 'foreman', label: 'Foreman', permissions: ['live_operations'] }
    ]
  },
  automotive: {
    owner: { id: 'dealer-principal', label: 'Dealer Principal' },
    manager: { id: 'dealership-manager', label: 'Dealership Manager' },
    roles: [{ id: 'sales-manager', label: 'Sales Manager', permissions: ['management', 'marketing_intelligence'] },
      { id: 'service-manager', label: 'Service Manager', permissions: ['live_operations', 'management'] },
      { id: 'parts-manager', label: 'Parts Manager', permissions: ['management'] },
      { id: 'finance-manager', label: 'F&I Manager', permissions: ['management', 'projections'] }
    ]
  },
  education: {
    owner: { id: 'school-director', label: 'School Director' },
    manager: { id: 'campus-manager', label: 'Campus Manager' },
    roles: [{ id: 'curriculum-coordinator', label: 'Curriculum Coordinator', permissions: ['management'] },
      { id: 'enrollment-manager', label: 'Enrollment Manager', permissions: ['live_operations', 'marketing_intelligence'] },
      { id: 'student-services', label: 'Student Services Lead', permissions: ['live_operations', 'management'] },
      { id: 'instructor-lead', label: 'Lead Instructor', permissions: ['management'] }
    ]
  },
  beauty: {
    owner: { id: 'salon-owner', label: 'Salon Owner' },
    manager: { id: 'salon-manager', label: 'Salon Manager' },
    roles: [{ id: 'senior-stylist', label: 'Senior Stylist', permissions: ['management'] },
      { id: 'beauty-director', label: 'Beauty Director', permissions: ['management', 'marketing_intelligence'] },
      { id: 'reception-manager', label: 'Reception Manager', permissions: ['live_operations'] },
      { id: 'retail-coordinator', label: 'Retail Coordinator', permissions: ['management'] }
    ]
  },
  manufacturing: {
    owner: { id: 'factory-owner', label: 'Factory Owner' },
    manager: { id: 'plant-manager', label: 'Plant Manager' },
    roles: [{ id: 'production-manager', label: 'Production Manager', permissions: ['live_operations', 'management'] },
      { id: 'quality-manager', label: 'Quality Manager', permissions: ['management'] },
      { id: 'maintenance-manager', label: 'Maintenance Manager', permissions: ['management'] },
      { id: 'shift-supervisor', label: 'Shift Supervisor', permissions: ['live_operations'] }
    ]
  },
  technology: {
    owner: { id: 'founder-ceo', label: 'Founder/CEO' },
    manager: { id: 'coo', label: 'COO' },
    roles: [{ id: 'engineering-lead', label: 'Engineering Lead', permissions: ['management'] },
      { id: 'product-manager', label: 'Product Manager', permissions: ['management', 'projections'] },
      { id: 'customer-success', label: 'Customer Success Lead', permissions: ['live_operations', 'marketing_intelligence'] },
      { id: 'growth-manager', label: 'Growth Manager', permissions: ['marketing_intelligence', 'projections'] },
      { id: 'hr-manager', label: 'HR Manager', permissions: ['management', 'staff'] },
      { id: 'team-lead', label: 'Team Lead', permissions: ['live_operations', 'management'] },
      { id: 'staff-member', label: 'Staff Member', permissions: ['live_operations'] },
      { id: 'operations-manager', label: 'Operations Manager', permissions: ['live_operations', 'management'] },
      { id: 'marketing-manager', label: 'Marketing Manager', permissions: ['marketing_intelligence'] },
      { id: 'sales-manager', label: 'Sales Manager', permissions: ['live_operations', 'management'] }
    ]
  },
  finance: {
    owner: { id: 'firm-principal', label: 'Firm Principal' },
    manager: { id: 'managing-director', label: 'Managing Director' },
    roles: [{ id: 'senior-advisor', label: 'Senior Advisor', permissions: ['management'] },
      { id: 'portfolio-manager', label: 'Portfolio Manager', permissions: ['management', 'projections'] },
      { id: 'compliance-officer', label: 'Compliance Officer', permissions: ['management'] },
      { id: 'client-relations', label: 'Client Relations Manager', permissions: ['live_operations', 'marketing_intelligence'] }
    ]
  },
  accounting: {
    owner: { id: 'firm-owner', label: 'Firm Owner' },
    manager: { id: 'practice-manager', label: 'Practice Manager' },
    roles: [{ id: 'senior-accountant', label: 'Senior Accountant', permissions: ['management'] },
      { id: 'tax-manager', label: 'Tax Manager', permissions: ['management', 'projections'] },
      { id: 'bookkeeper-lead', label: 'Lead Bookkeeper', permissions: ['live_operations', 'management'] },
      { id: 'client-manager', label: 'Client Manager', permissions: ['live_operations', 'marketing_intelligence'] }
    ]
  },
  accommodation: {
    owner: { id: 'hotel-owner', label: 'Hotel Owner' },
    manager: { id: 'hotel-manager', label: 'Hotel Manager' },
    roles: [{ id: 'front-desk-manager', label: 'Front Desk Manager', permissions: ['live_operations', 'management'] },
      { id: 'housekeeping-manager', label: 'Housekeeping Manager', permissions: ['management'] },
      { id: 'revenue-manager', label: 'Revenue Manager', permissions: ['management', 'projections'] },
      { id: 'concierge-lead', label: 'Concierge Lead', permissions: ['live_operations'] }
    ]
  },
  logistics: {
    owner: { id: 'company-owner', label: 'Company Owner' },
    manager: { id: 'operations-manager', label: 'Operations Manager' },
    roles: [{ id: 'dispatch-manager', label: 'Dispatch Manager', permissions: ['live_operations', 'management'] },
      { id: 'fleet-manager', label: 'Fleet Manager', permissions: ['management'] },
      { id: 'warehouse-manager', label: 'Warehouse Manager', permissions: ['management'] },
      { id: 'route-planner', label: 'Route Planner', permissions: ['live_operations', 'projections'] }
    ]
  },
  trades: {
    owner: { id: 'business-owner', label: 'Business Owner' },
    manager: { id: 'operations-manager', label: 'Operations Manager' },
    roles: [{ id: 'lead-tradesman', label: 'Lead Tradesman', permissions: ['live_operations', 'management'] },
      { id: 'estimator', label: 'Estimator', permissions: ['management', 'projections'] },
      { id: 'scheduler', label: 'Scheduler', permissions: ['live_operations'] },
      { id: 'apprentice-coordinator', label: 'Apprentice Coordinator', permissions: ['management'] }
    ]
  },
  legal: {
    owner: { id: 'senior-partner', label: 'Senior Partner' },
    manager: { id: 'managing-partner', label: 'Managing Partner' },
    roles: [{ id: 'associate-partner', label: 'Associate Partner', permissions: ['management'] },
      { id: 'practice-manager', label: 'Practice Manager', permissions: ['live_operations', 'management'] },
      { id: 'paralegal-lead', label: 'Paralegal Lead', permissions: ['management'] },
      { id: 'billing-coordinator', label: 'Billing Coordinator', permissions: ['management', 'projections'] }
    ]
  },
  entertainment: {
    owner: { id: 'venue-owner', label: 'Venue Owner' },
    manager: { id: 'venue-manager', label: 'Venue Manager' },
    roles: [{ id: 'event-coordinator', label: 'Event Coordinator', permissions: ['live_operations', 'management'] },
      { id: 'box-office-manager', label: 'Box Office Manager', permissions: ['management'] },
      { id: 'production-manager', label: 'Production Manager', permissions: ['management'] },
      { id: 'marketing-manager', label: 'Marketing Manager', permissions: ['marketing_intelligence'] }
    ]
  },
  agriculture: {
    owner: { id: 'farm-owner', label: 'Farm Owner' },
    manager: { id: 'farm-manager', label: 'Farm Manager' },
    roles: [{ id: 'operations-supervisor', label: 'Operations Supervisor', permissions: ['live_operations', 'management'] },
      { id: 'crop-manager', label: 'Crop Manager', permissions: ['management'] },
      { id: 'equipment-manager', label: 'Equipment Manager', permissions: ['management'] },
      { id: 'harvest-coordinator', label: 'Harvest Coordinator', permissions: ['live_operations'] }
    ]
  },
  gaming: {
    owner: { id: 'venue-owner', label: 'Gaming Venue Owner' },
    manager: { id: 'gaming-manager', label: 'Gaming Manager' },
    roles: [{ id: 'floor-manager', label: 'Floor Manager', permissions: ['live_operations', 'management'] },
      { id: 'compliance-manager', label: 'Compliance Manager', permissions: ['management'] },
      { id: 'cashier-supervisor', label: 'Cashier Supervisor', permissions: ['management'] },
      { id: 'surveillance-manager', label: 'Surveillance Manager', permissions: ['live_operations'] }
    ]
  },
  environmental: {
    owner: { id: 'company-director', label: 'Company Director' },
    manager: { id: 'operations-director', label: 'Operations Director' },
    roles: [{ id: 'project-manager', label: 'Project Manager', permissions: ['live_operations', 'management'] },
      { id: 'environmental-scientist', label: 'Lead Scientist', permissions: ['management'] },
      { id: 'compliance-specialist', label: 'Compliance Specialist', permissions: ['management'] },
      { id: 'field-coordinator', label: 'Field Coordinator', permissions: ['live_operations'] }
    ]
  },
  // === QSR (Quick Service Restaurant) ===
  qsr: {
    owner: { id: 'director', label: 'Director' },
    manager: { id: 'area-manager', label: 'Area Manager' },
    roles: [{ id: 'store-manager', label: 'Store Manager', permissions: ['live_operations', 'management'] },
      { id: 'shift-supervisor', label: 'Shift Supervisor', permissions: ['live_operations'] },
      { id: 'kitchen-manager', label: 'Kitchen Manager', permissions: ['management'] },
      { id: 'drive-through-lead', label: 'Drive-Through Lead', permissions: ['live_operations'] },
      { id: 'training-coordinator', label: 'Training Coordinator', permissions: ['management'] },
      { id: 'compliance-officer', label: 'Compliance Officer', permissions: ['management', 'compliance'] }
    ]
  },
  // === 9 NEW FRANCHISE INDUSTRIES ===
  cleaning: {
    owner: { id: 'cleaning-business-owner', label: 'Business Owner' },
    manager: { id: 'operations-manager', label: 'Operations Manager' },
    roles: [{ id: 'account-manager', label: 'Account Manager', permissions: ['management', 'marketing_intelligence'] },
      { id: 'site-supervisor', label: 'Site Supervisor', permissions: ['live_operations', 'management'] },
      { id: 'quality-inspector', label: 'Quality Inspector', permissions: ['management'] },
      { id: 'team-lead', label: 'Team Lead', permissions: ['live_operations'] }
    ]
  },
  pet_services: {
    owner: { id: 'pet-business-owner', label: 'Business Owner' },
    manager: { id: 'facility-manager', label: 'Facility Manager' },
    roles: [{ id: 'lead-groomer', label: 'Lead Groomer', permissions: ['live_operations', 'management'] },
      { id: 'kennel-supervisor', label: 'Kennel Supervisor', permissions: ['live_operations'] },
      { id: 'veterinary-tech', label: 'Veterinary Technician', permissions: ['management'] },
      { id: 'reception-coordinator', label: 'Reception Coordinator', permissions: ['live_operations'] }
    ]
  },
  childcare: {
    owner: { id: 'center-director', label: 'Center Director/Owner' },
    manager: { id: 'center-manager', label: 'Center Manager' },
    roles: [{ id: 'educational-leader', label: 'Educational Leader', permissions: ['management'] },
      { id: 'room-leader', label: 'Room Leader', permissions: ['live_operations', 'management'] },
      { id: 'compliance-coordinator', label: 'Compliance Coordinator', permissions: ['management'] },
      { id: 'family-liaison', label: 'Family Liaison', permissions: ['live_operations', 'marketing_intelligence'] }
    ]
  },
  senior_care: {
    owner: { id: 'care-provider-owner', label: 'Care Provider Owner' },
    manager: { id: 'care-manager', label: 'Care Manager' },
    roles: [{ id: 'clinical-coordinator', label: 'Clinical Coordinator', permissions: ['management'] },
      { id: 'care-supervisor', label: 'Care Supervisor', permissions: ['live_operations', 'management'] },
      { id: 'scheduling-coordinator', label: 'Scheduling Coordinator', permissions: ['live_operations'] },
      { id: 'quality-compliance', label: 'Quality & Compliance Lead', permissions: ['management'] }
    ]
  },
  pharmacy: {
    owner: { id: 'pharmacy-owner', label: 'Pharmacy Owner' },
    manager: { id: 'pharmacy-manager', label: 'Pharmacy Manager' },
    roles: [{ id: 'lead-pharmacist', label: 'Lead Pharmacist', permissions: ['management'] },
      { id: 'dispensary-manager', label: 'Dispensary Manager', permissions: ['live_operations', 'management'] },
      { id: 'retail-supervisor', label: 'Retail Supervisor', permissions: ['live_operations'] },
      { id: 'compliance-officer', label: 'Compliance Officer', permissions: ['management'] }
    ]
  },
  staffing: {
    owner: { id: 'agency-owner', label: 'Agency Owner' },
    manager: { id: 'branch-manager', label: 'Branch Manager' },
    roles: [{ id: 'senior-recruiter', label: 'Senior Recruiter', permissions: ['management'] },
      { id: 'account-manager', label: 'Account Manager', permissions: ['live_operations', 'marketing_intelligence'] },
      { id: 'onboarding-specialist', label: 'Onboarding Specialist', permissions: ['live_operations'] },
      { id: 'compliance-coordinator', label: 'Compliance Coordinator', permissions: ['management'] }
    ]
  },
  printing: {
    owner: { id: 'print-shop-owner', label: 'Print Shop Owner' },
    manager: { id: 'production-manager', label: 'Production Manager' },
    roles: [{ id: 'prepress-lead', label: 'Prepress Lead', permissions: ['management'] },
      { id: 'print-supervisor', label: 'Print Supervisor', permissions: ['live_operations', 'management'] },
      { id: 'sales-consultant', label: 'Sales Consultant', permissions: ['live_operations', 'marketing_intelligence'] },
      { id: 'finishing-lead', label: 'Finishing Lead', permissions: ['live_operations'] }
    ]
  },
  pest_control: {
    owner: { id: 'pest-control-owner', label: 'Business Owner' },
    manager: { id: 'operations-manager', label: 'Operations Manager' },
    roles: [{ id: 'lead-technician', label: 'Lead Technician', permissions: ['live_operations', 'management'] },
      { id: 'commercial-manager', label: 'Commercial Account Manager', permissions: ['management', 'marketing_intelligence'] },
      { id: 'scheduling-coordinator', label: 'Scheduling Coordinator', permissions: ['live_operations'] },
      { id: 'training-coordinator', label: 'Training Coordinator', permissions: ['management'] }
    ]
  },
  moving_storage: {
    owner: { id: 'moving-company-owner', label: 'Company Owner' },
    manager: { id: 'operations-manager', label: 'Operations Manager' },
    roles: [{ id: 'dispatch-manager', label: 'Dispatch Manager', permissions: ['live_operations', 'management'] },
      { id: 'crew-leader', label: 'Crew Leader', permissions: ['live_operations'] },
      { id: 'storage-manager', label: 'Storage Facility Manager', permissions: ['management'] },
      { id: 'sales-estimator', label: 'Sales & Estimating', permissions: ['live_operations', 'marketing_intelligence'] }
    ]
  },
  // Neutral, industry-agnostic role set. Used for REAL accounts whose record has
  // no recognised industry — so they never inherit the hospitality "Venue
  // Operator" demo role. Generic owner/manager + universal staff roles only.
  general: {
    owner: { id: 'business-owner', label: 'Business Owner' },
    manager: { id: 'operations-manager', label: 'Operations Manager' },
    roles: [{ id: 'team-lead', label: 'Team Lead', permissions: ['live_operations', 'management'] },
      { id: 'hr-manager', label: 'HR Manager', permissions: ['management', 'staff'] },
      { id: 'marketing-manager', label: 'Marketing Manager', permissions: ['marketing_intelligence'] },
      { id: 'sales-manager', label: 'Sales Manager', permissions: ['live_operations', 'management'] },
      { id: 'staff-member', label: 'Staff Member', permissions: ['live_operations'] }
    ]
  }
};

// Universal roles that apply to all industries
const UNIVERSAL_ROLES = [{ id: 'accountant', label: 'Accountant', permissions: ['management', 'projections'] },
  { id: 'advisor', label: 'Business Advisor', permissions: ['live_operations', 'management', 'market_intelligence', 'marketing_intelligence', 'projections', 'business_search', 'view_overview'] }
];

// Map industry keys that aren't direct INDUSTRY_ROLES entries to the closest
// configured industry (mirrors industryDemoData's CANONICAL_TO_LEGACY) so the
// owner title / role set never silently falls back to the hospitality default
// (e.g. "Venue Operator (Owner)" leaking onto an IT-services account).
const INDUSTRY_ROLE_ALIASES = {
  it_services: 'technology',
  saas: 'technology',
  software: 'technology',
  media_telecom: 'technology',
  media: 'technology',
  wholesale: 'retail',
  mining: 'manufacturing',
  utilities: 'manufacturing',
  admin_services: 'professional',
  public_admin: 'professional',
  advisory: 'professional',
  consulting: 'professional',
  consultancy: 'professional',
  forensic: 'accounting',
  forensic_accounting: 'accounting',
  bookkeeping: 'accounting',
  financial_services: 'finance',
};

export const resolveIndustryRoleKey = (industry) => {
  const key = (industry || '').toLowerCase();
  if (INDUSTRY_ROLES[key]) return key;
  const alias = INDUSTRY_ROLE_ALIASES[key];
  if (alias && INDUSTRY_ROLES[alias]) return alias;
  // Neutral default for REAL accounts with an unrecognised industry — never
  // leak the hospitality "Venue Operator" role onto an advisor/Resolve account.
  return 'general';
};


// Function to get roles for a specific industry
export const getIndustryRoles = (industry) => {
  const industryKey = resolveIndustryRoleKey(industry);
  const industryConfig = INDUSTRY_ROLES[industryKey] || INDUSTRY_ROLES.general;

  const allRoles = [{ ...industryConfig.owner, permissions: ['live_operations', 'management', 'market_intelligence', 'marketing_intelligence', 'projections', 'business_search', 'view_overview'] },
    { ...industryConfig.manager, permissions: ['live_operations', 'management', 'marketing_intelligence', 'projections'] },
    ...industryConfig.roles,
    ...UNIVERSAL_ROLES
  ];

  // Iter-276 — de-duplicate by role id. Several industries (hospitality,
  // retail, etc.) historically listed "accountant" in their per-industry
  // roles array AND in UNIVERSAL_ROLES, producing duplicate React keys
  // whenever the list was rendered (e.g. owner role selector on
  // `/app/dashboard`). Keep the FIRST occurrence so industry-specific
  // permissions take precedence over the universal defaults.
  const seen = new Set();
  return allRoles.filter((role) => {
    if (!role?.id || seen.has(role.id)) return false;
    seen.add(role.id);
    return true;
  });
};

// Legacy CLIENT_TYPES for backwards compatibility (defaults to hospitality)
export const CLIENT_TYPES = {
  'VENUE_OPERATOR': { 
    id: 'venue-operator', 
    label: 'Venue Operator (Owner)',
    permissions: ['live_operations', 'management', 'market_intelligence', 'marketing_intelligence', 'projections', 'business_search', 'view_overview']
  },
  'GENERAL_MANAGER': {
    id: 'general-manager',
    label: 'General Manager',
    permissions: ['live_operations', 'management', 'marketing_intelligence', 'projections']
  },
  'KITCHEN_MANAGER': {
    id: 'kitchen-manager',
    label: 'Kitchen Manager',
    permissions: ['management']
  },
  'FOH_MANAGER': {
    id: 'foh-manager',
    label: 'FOH Manager',
    permissions: ['live_operations', 'management', 'marketing_intelligence']
  },
  'BAR_MANAGER': {
    id: 'bar-manager',
    label: 'Bar Manager',
    permissions: ['management']
  },
  'ACCOUNTANT': { 
    id: 'accountant', 
    label: 'Accountant',
    permissions: ['management', 'projections']
  },
  'SHIFT_SUPERVISOR': {
    id: 'shift-supervisor',
    label: 'Shift Supervisor',
    permissions: ['live_operations']
  },
  'BRAND': { 
    id: 'brand', 
    label: 'Brand Partner',
    permissions: ['market_intelligence', 'marketing_intelligence', 'business_search']
  },
  'DISTRIBUTOR': { 
    id: 'distributor', 
    label: 'Distributor',
    permissions: ['market_intelligence', 'business_search']
  },
  'INVESTOR': { 
    id: 'investor', 
    label: 'Investor',
    permissions: ['management', 'projections', 'market_intelligence']
  },
  'ADVISOR': { 
    id: 'advisor', 
    label: 'Business Advisor',
    permissions: ['live_operations', 'management', 'market_intelligence', 'marketing_intelligence', 'projections', 'business_search']
  }
};

const ClientContext = createContext();

// Get initial industry from localStorage or default to Hospitality
const getInitialIndustry = () => {
  try {
    const saved = localStorage.getItem('corvana_selected_industry');
    if (saved && INDUSTRY_ROLES[saved.toLowerCase()]) {
      return saved;
    }
  } catch (e) {
    console.warn('Failed to read industry from localStorage:', e);
  }
  return 'Hospitality';
};

// Get initial role from localStorage or default based on industry
const getInitialRole = (industry) => {
  try {
    const savedRole = localStorage.getItem('corvana_selected_role');
    if (savedRole) {
      const parsed = JSON.parse(savedRole);
      const roles = getIndustryRoles(industry);
      const matchingRole = roles.find(r => r.id === parsed.id);
      if (matchingRole) {
        return matchingRole;
      }
    }
  } catch (e) {
    console.warn('Failed to read role from localStorage:', e);
  }
  // Return the owner role for the industry
  const industryKey = resolveIndustryRoleKey(industry);
  const industryConfig = INDUSTRY_ROLES[industryKey] || INDUSTRY_ROLES.general;
  return {
    ...industryConfig.owner,
    permissions: ['live_operations', 'management', 'market_intelligence', 'marketing_intelligence', 'projections', 'business_search']
  };
};

export function ClientProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const initialIndustry = getInitialIndustry();
  const [clientType, setClientTypeState] = useState(() => getInitialRole(initialIndustry));
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedState, setSelectedState] = useState('NSW');
  const [selectedSuburb, setSelectedSuburb] = useState('Surry Hills');
  
  // Multi-location state for QSR and other multi-site businesses
  const [currentLocation, setCurrentLocationState] = useState(null);
  
  // Module toggle state — which intelligence modules are active for this account
  const [enabledModules, setEnabledModulesState] = useState(() => {
    try {
      const saved = localStorage.getItem('corvana_enabled_modules');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    // Default: all modules from the QSR demo
    const industryKey = initialIndustry?.toLowerCase() || 'hospitality';
    const demoCustomer = DEMO_CUSTOMERS[industryKey];
    return demoCustomer?.modules || ALL_MODULES.map(m => m.id);
  });
  
  const [modulePresets, setModulePresets] = useState([]);
  const [activePreset, setActivePreset] = useState(null);

  // Save modules to both localStorage and backend
  const saveModulesToBackend = useCallback(async (modules) => {
    try {
      localStorage.setItem('corvana_enabled_modules', JSON.stringify(modules));
    } catch (e) { /* ignore */ }
    try {
      await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/modules/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules })
      });
    } catch (e) { /* non-blocking */ }
  }, []);

  // Toggle a single module on/off
  const toggleModule = useCallback((moduleId) => {
    setEnabledModulesState(prev => {
      const newModules = prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId];
      setActivePreset(null);
      saveModulesToBackend(newModules);
      return newModules;
    });
  }, [saveModulesToBackend]);
  
  // Set all modules at once
  const setEnabledModules = useCallback((modules) => {
    setEnabledModulesState(modules);
    setActivePreset(null);
    saveModulesToBackend(modules);
  }, [saveModulesToBackend]);

  // Apply a preset configuration
  const applyPreset = useCallback(async (presetId) => {
    try {
      const res = await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/modules/presets/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preset_id: presetId })
      });
      if (res.ok) {
        const data = await res.json();
        setEnabledModulesState(data.modules);
        setActivePreset(presetId);
        try {
          localStorage.setItem('corvana_enabled_modules', JSON.stringify(data.modules));
        } catch (e) { /* ignore */ }
        return data;
      }
    } catch (e) { /* non-blocking */ }
    // Fallback: apply from local presets
    const preset = modulePresets.find(p => p.id === presetId);
    if (preset) {
      setEnabledModulesState(preset.modules);
      setActivePreset(presetId);
      saveModulesToBackend(preset.modules);
    }
  }, [modulePresets, saveModulesToBackend]);

  // Load presets and saved config from backend on mount
  useEffect(() => {
    // Only fetch once a user session is established — these endpoints are
    // user-scoped and 401 otherwise (e.g. on the Tech Panel, which has no
    // user session). Gating avoids first-paint 401 console noise.
    if (!isAuthenticated) return;
    const loadFromBackend = async () => {
      try {
        // Load presets — cookie auth via credentials:'include'.
        const presetsRes = await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/modules/presets`, {
          credentials: 'include',
        });
        if (presetsRes.ok) {
          const data = await presetsRes.json();
          setModulePresets(data.presets || []);
        }
        // Load saved config
        const configRes = await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/modules/config`, {
          credentials: 'include',
        });
        if (configRes.ok) {
          const config = await configRes.json();
          if (config.modules && config.modules.length > 0) {
            setEnabledModulesState(config.modules);
            setActivePreset(config.preset || null);
            try {
              localStorage.setItem('corvana_enabled_modules', JSON.stringify(config.modules));
            } catch (e) { /* ignore */ }
          }
        }
      } catch (e) { /* non-blocking */ }
    };
    loadFromBackend();
  }, [isAuthenticated]);
  
  // Customer business configuration with localStorage persistence for industry
  const initialDemoKey = initialIndustry?.toLowerCase() || 'hospitality';
  const initialDemo = DEMO_CUSTOMERS[initialDemoKey] || DEMO_CUSTOMERS.hospitality;
  const [customerBusiness, setCustomerBusinessState] = useState({
    name: initialDemo?.company || 'The Continental',
    business_id: initialDemo?.business_id || 'continental_demo_001',
    industry: initialIndustry,
    location: {
      state: 'NSW',
      suburb: 'Surry Hills',
      address: '132-134 Commonwealth Street, Surry Hills NSW 2010'
    },
    businessType: 'Hotel with Gaming',
    capacity: 200,
    establishedYear: 2010,
    hasCRMAccess: true,
    reviewPlatforms: {
      google: 'https://g.page/r/YOUR_PLACE_ID/review',
      tripadvisor: 'https://www.tripadvisor.com/YOUR_BUSINESS',
      yelp: 'https://www.yelp.com/biz/YOUR_BUSINESS',
      facebook: 'https://www.facebook.com/YOUR_PAGE/reviews'
    }
  });
  
  // Wrapper to persist clientType to localStorage
  const setClientType = (newType) => {
    setClientTypeState(newType);
    try {
      localStorage.setItem('corvana_selected_role', JSON.stringify({ id: newType.id, label: newType.label }));
    } catch (e) {
      console.warn('Failed to save role to localStorage:', e);
    }
  };
  
  // Wrapper to persist industry to localStorage
  const setCustomerBusiness = (updater) => {
    setCustomerBusinessState(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      // Persist industry to localStorage
      if (newState.industry && newState.industry !== prev.industry) {
        try {
          localStorage.setItem('corvana_selected_industry', newState.industry);
        } catch (e) {
          console.warn('Failed to save industry to localStorage:', e);
        }
      }
      return newState;
    });
  };
  
  // Get available roles based on current industry
  const availableRoles = useMemo(() => {
    return getIndustryRoles(customerBusiness.industry);
  }, [customerBusiness.industry]);
  
  // Multi-location: available locations for QSR industry
  const availableLocations = useMemo(() => {
    const industryKey = customerBusiness.industry?.toLowerCase();
    if (industryKey === 'qsr') {
      return QSR_DEMO_LOCATIONS;
    }
    return [];
  }, [customerBusiness.industry]);
  
  // Set current location with localStorage persistence
  const setCurrentLocation = (location) => {
    setCurrentLocationState(location);
    try {
      localStorage.setItem('corvana_current_location', location ? JSON.stringify({ id: location.id, shortName: location.shortName }) : '');
    } catch (e) {
      console.warn('Failed to save location to localStorage:', e);
    }
  };
  
  // Reset location when switching away from multi-location industry
  useEffect(() => {
    const industryKey = customerBusiness.industry?.toLowerCase();
    if (industryKey === 'qsr') {
      // Restore from localStorage or default to 'all'
      try {
        const saved = localStorage.getItem('corvana_current_location');
        if (saved) {
          const parsed = JSON.parse(saved);
          const match = QSR_DEMO_LOCATIONS.find(l => l.id === parsed.id);
          if (match) {
            setCurrentLocationState(match);
            return;
          }
        }
      } catch (e) { /* ignore */ }
      setCurrentLocationState(null); // null = "All Locations"
    } else {
      setCurrentLocationState(null);
    }
  }, [customerBusiness.industry]);
  
  // Reset enabled modules when industry changes to match the demo account defaults
  useEffect(() => {
    const industryKey = customerBusiness.industry?.toLowerCase();
    const demoCustomer = DEMO_CUSTOMERS[industryKey];
    if (demoCustomer) {
      const modules = demoCustomer.modules || ALL_MODULES.map(m => m.id);
      setEnabledModulesState(modules);
      try {
        localStorage.setItem('corvana_enabled_modules', JSON.stringify(modules));
      } catch (e) { /* ignore */ }
    }
  }, [customerBusiness.industry]);

  // Iter-295 — Sync the active client/business context with the
  // currently-logged-in user. Without this, a tenant whose user record
  // says industry='qsr' would still get the localStorage-cached
  // 'Hospitality' (Continental) context, leaking another tenant's
  // branding into their dashboard. Fail-closed: if the user is logged
  // in but the backend has not populated industry yet, we keep the
  // user's company/business_id but don't fall through to a sibling.
  const { user: authUser} = useAuth();
  useEffect(() => {
    if (!authUser) return;
    const isDemo = authUser.is_demo === true;
    const userIndustry = authUser.industry || authUser.industry_category;
    const userBizId = authUser.business_id;
    const userCompany = authUser.company_name;
    setCustomerBusinessState(prev => {
      const next = { ...prev };
      if (userBizId && userBizId !== prev.business_id) next.business_id = userBizId;
      // Business name — a REAL (non-demo) account must NEVER inherit the demo
      // venue name ("The Continental"). Use their company, else a neutral label.
      if (userCompany) {
        if (userCompany !== prev.name) next.name = userCompany;
      } else if (!isDemo && prev.name !== 'Your Business') {
        next.name = 'Your Business';
      }
      // Industry — a REAL account must NEVER inherit the demo 'Hospitality'
      // default (which would surface "Venue Operator" + food/liquor/gaming
      // language). Map their saved industry, else fall back to the NEUTRAL
      // 'General' role/terminology set. Demo accounts keep their rich industry.
      if (userIndustry) {
        const norm = String(userIndustry).toLowerCase();
        const titled = norm.charAt(0).toUpperCase() + norm.slice(1);
        if (titled !== prev.industry) {
          next.industry = titled;
          try { localStorage.setItem('corvana_selected_industry', titled); } catch (e) { /* ignore */ }
        }
      } else if (!isDemo && prev.industry !== 'General') {
        next.industry = 'General';
        try { localStorage.setItem('corvana_selected_industry', 'General'); } catch (e) { /* ignore */ }
      }
      return next;
    });
  // eslint-disable-next-line
  }, [authUser?.industry, authUser?.industry_category, authUser?.business_id, authUser?.company_name, authUser?.is_demo]);

  // Real (non-demo) accounts: load the actual staff roster so the role
  // selector can show each teammate by name + role instead of generic
  // industry role templates. Demo accounts keep the persona templates.
  useEffect(() => {
    if (!isAuthenticated || !authUser || authUser.is_demo === true) {
      setTeamMembers([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/account/team`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTeamMembers(Array.isArray(data.team_members) ? data.team_members : []);
      } catch (e) { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [isAuthenticated, authUser?.email, authUser?.is_demo]);

  // Map real teammates → selector options ("Name · Role") with a sensible
  // permission scope so an owner/manager can preview a staff member's view.
  const staffRoleOptions = useMemo(() => {
    const OWNER_PERMS = ['live_operations', 'management', 'market_intelligence', 'marketing_intelligence', 'projections', 'business_search', 'view_overview'];
    const MANAGER_PERMS = ['live_operations', 'management', 'marketing_intelligence', 'projections'];
    return teamMembers
      .filter((m) => m && (m.first_name || m.last_name || m.email))
      .map((m) => {
        const name = [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || m.email;
        const roleKey = String(m.role || 'staff').toLowerCase();
        const roleLabel = roleKey.charAt(0).toUpperCase() + roleKey.slice(1);
        const permissions = (roleKey === 'owner' || roleKey === 'admin')
          ? OWNER_PERMS
          : roleKey === 'manager'
            ? MANAGER_PERMS
            : (Array.isArray(m.permissions) && m.permissions.length ? m.permissions : ['live_operations']);
        return { id: `staff_${m.member_id}`, label: `${name} · ${roleLabel}`, name, roleLabel, permissions, isStaff: true };
      });
  }, [teamMembers]);

  
  // Update client type when industry changes to use appropriate owner role
  useEffect(() => {
    const industryKey = resolveIndustryRoleKey(customerBusiness.industry);
    const industryConfig = INDUSTRY_ROLES[industryKey] || INDUSTRY_ROLES.general;
    
    // If current role doesn't exist in new industry, reset to owner
    const currentRoleExists = availableRoles.some(r => r.id === clientType.id);
    if (!currentRoleExists) {
      setClientType({
        ...industryConfig.owner,
        permissions: ['live_operations', 'management', 'market_intelligence', 'marketing_intelligence', 'projections', 'business_search']
      });
    }
  }, [customerBusiness.industry, availableRoles, clientType.id]);
  
  // Platform updates tracking
  const [lastSeenUpdate, setLastSeenUpdate] = useState(
    localStorage.getItem('corvana_last_seen_update') || '2024-11-01'
  );
  
  const markUpdatesAsSeen = (updateId) => {
    setLastSeenUpdate(updateId);
    localStorage.setItem('corvana_last_seen_update', updateId);
  };

  return (
    <ClientContext.Provider value={{ 
      clientType, 
      setClientType,
      selectedState,
      setSelectedState,
      selectedSuburb,
      setSelectedSuburb,
      customerBusiness,
      setCustomerBusiness,
      availableRoles,
      staffRoleOptions,
      teamMembers,
      currentLocation,
      setCurrentLocation,
      availableLocations,
      enabledModules,
      toggleModule,
      setEnabledModules,
      modulePresets,
      activePreset,
      applyPreset,
      lastSeenUpdate,
      markUpdatesAsSeen
    }}>
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
}
